import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { CustomValidators, StoreLoader, Unsubscriber } from '@core-lib/classes';
import { AppConstants } from '@core-lib/config';
import { ActionType, TiersType, TypeImmatriculation } from '@core-lib/enums';
import { RegistreType } from '@core-lib/enums/pays.enum';
import { UserPermission } from '@core-lib/enums/user-permission.enum';
import { dateToUTC, extractFormControlNames, formatDate, markFormGroupTouched, markFormGroupUntouched } from '@core-lib/functions';
import { userHasPermission } from '@core-lib/functions/user.function';
import {
	IAction,
	IAdresse,
	INameBySiret,
	IPays,
	ITierPersonneMoraleForm,
	ITierPersonnePhysiqueForm,
	ITiersPersonneMoraleForm,
	ITiersPersonnePhysiqueForm,
	ITiersWithAdress
} from '@core-lib/models';
import { TiersService } from '@core-lib/services/tiers.service';
import { TiersStoreActions, TiersStoreSelectors } from '@core-lib/store/tiers-store';
import { selectUserPermissions } from '@core-lib/store/user-store/selectors';
import { ObjectPropertyNamesHolder } from '@core-lib/types';
import { Store } from '@ngrx/store';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Observable, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export const formPersonnePhysiqueControlNames = (name: keyof ITiersPersonnePhysiqueForm) => name;
export const formPersonneMoraleControlNames = (name: keyof ITiersPersonneMoraleForm) => name;

// Comme Validators.required, mais rejette aussi les chaines composees uniquement d'espaces.
// Produit la meme cle d'erreur ('required') pour reutiliser le rendu existant de form-control-feedback.
const requiredNotBlank = (control: AbstractControl): ValidationErrors | null =>
	control.value == null || (typeof control.value === 'string' && control.value.trim().length === 0) ? { required: true } : null;

@Component({
	selector: 'tiers-form',
	templateUrl: './tiers-form.component.html',
	styleUrl: './tiers-form.component.scss',
	standalone: false
})
export class TiersFormComponent extends Unsubscriber implements OnInit {
	@Input() loader: StoreLoader;
	@Input() personnePhysiqueModel: ITiersPersonnePhysiqueForm;
	@Input() set nationalites$(value: Observable<IPays[]>) {
		if (value) {
			value.pipe(takeUntil(this.destroySubscriptions$)).subscribe(pays => {
				this.nationalites.push(pays?.find(x => x.codeISO == 'FR'));
				this.nationalites = this.nationalites.concat(pays?.filter(x => x.codeISO != 'FR'));
			});

			this._nationalites$ = value;
		}
	}
	get nationalites$(): Observable<IPays[]> {
		return this._nationalites$;
	}
	_nationalites$: Observable<IPays[]>;

	@ViewChild('denominationCommerciale') denominationCommerciale: ElementRef;
	tempId: string = null;

	@Output() action: EventEmitter<IAction> = new EventEmitter();
	@Input() labelNature?: string;
	@Input() selectedPay: string;
	@Input() set tiers(value: ITiersWithAdress) {
		if (value) {
			this.buildConsultForm(value);
			this.tempId = value.tempId;
		}
	}
	@Input() libelleKey: string;
	get isToConsult(): boolean {
		return this._isToConsult;
	}
	_isToConsult: boolean;
	@Input() set isToConsult(value: boolean) {
		this._isToConsult = value;
		if (value == false) {
			this.form.enable();
		}
	}

	get isToUpdate(): boolean {
		return this._isToUpdate;
	}
	_isToUpdate: boolean;
	@Input() set isToUpdate(value: boolean) {
		this._isToUpdate = value;
		if (value == true) {
			this.paysIdField.valueChanges.pipe(takeUntil(this.destroySubscriptions$)).subscribe(() => {
				if (this.form.pristine) return;

				this.form.patchValue({ registre: null });
				this.setSirenValidators();
			});
			this.store$
				.select(selectUserPermissions)
				.pipe(takeUntil(this.destroySubscriptions$))
				.subscribe(permissions => {
					if (!userHasPermission(permissions, UserPermission.IsAdminEmdf)) {
						this.prenomField?.disable();
						this.nomField?.disable();
						this.libelleField?.disable();
						this.isPersonnePhysique ? null : this.paysIdField?.disable();
						this.registreField?.disable();
						this.disableImmatriculationRadios = true;
						// Set the tooltip text
						this.disabledFieldsTooltip = userHasPermission(permissions, UserPermission.IsAvocat)
							? 'Merci de contacter la Carpa.'
							: 'Merci de contacter un Administrateur.';
					}
					this.form?.controls['type']?.disable();
				});
		}
	}

	formatDate = formatDate;
	form: FormGroup<ITierPersonnePhysiqueForm> | FormGroup<ITierPersonneMoraleForm>;
	formControlNames: ObjectPropertyNamesHolder<ITiersPersonnePhysiqueForm | ITiersPersonneMoraleForm>;

	dateNaissanceMaxDate: Date = new Date(new Date().getTime() - 86400000);

	isPersonnePhysique: boolean = false;
	isPersonneMorale: boolean = false;
	isSirenRequired: boolean = false;
	isPaysSiren: boolean = false;
	isPaysParentFr: boolean = false;
	isPaysFr: boolean = false;
	isSirenSelected: boolean = true;
	isRegEqSelected: boolean = false;
	isImoSelected: boolean = false;
	disableImmatriculationRadios: boolean = false;
	immatriculationErrorMessage: string = '';
	wrongRCSFormatErrorMessage: string = 'ValidationErrors.WrongRcsFormat';
	wrongSirenLength: string;
	showValidationErrorMessage: boolean = true;
	registreValue: string = '';
	registreType: RegistreType = RegistreType.RegistreEquivalent;
	luhnErrorMessage: string = '';
	nationalites: IPays[] = [];
	tiersType = TiersType;
	currentAdresse: IAdresse;
	separateDialCode = false;
	selectedCountryISO: string = 'fr';
	disabledFieldsTooltip: string = null;
	isSubmited: boolean = false;
	isBirthDateRequired: boolean = true;
	paysParentMaxLengthSiren = { alpha: [6], numeric: [7, 9] };
	inputRestrictionValue: string | null;

	sirenSearchErrorMessage: string | null = null;
	isSearchingBySiren = false;
	maxRegistreFielLength: number = 9;

	get prenomField(): AbstractControl {
		return (this.form as FormGroup<ITierPersonnePhysiqueForm>)?.controls?.prenom;
	}

	get nomField(): AbstractControl {
		return (this.form as FormGroup<ITierPersonnePhysiqueForm>)?.controls?.nom;
	}

	get telephoneField(): AbstractControl {
		return this.form?.controls?.telephone;
	}
	get emailField(): AbstractControl {
		return this.form?.controls?.email;
	}

	get dateNaissanceField(): AbstractControl {
		return (this.form as FormGroup<ITierPersonnePhysiqueForm>)?.controls?.dateNaissance;
	}

	get paysIdField(): AbstractControl {
		return this.form?.controls?.paysId;
	}

	get isBirthDateUnknownField(): AbstractControl {
		return (this.form as FormGroup<ITierPersonnePhysiqueForm>)?.controls?.isBirthDateUnknown;
	}

	get registreField(): AbstractControl {
		return (this.form as FormGroup<ITierPersonneMoraleForm>)?.controls?.registre;
	}

	get typeImmatriculationField(): AbstractControl {
		return (this.form as FormGroup<ITierPersonneMoraleForm>)?.controls?.typeImmatriculation;
	}

	get libelleField(): AbstractControl {
		return (this.form as FormGroup<ITierPersonneMoraleForm>)?.controls?.libelle;
	}

	get formeJuridiqueField(): AbstractControl {
		return (this.form as FormGroup<ITierPersonneMoraleForm>)?.controls?.formeJuridique;
	}

	get denominationCommercialeField(): AbstractControl {
		return (this.form as FormGroup<ITierPersonneMoraleForm>)?.controls?.denominationCommerciale;
	}

	get institutionnelDroitPublicField(): AbstractControl {
		return (this.form as FormGroup<ITierPersonneMoraleForm>)?.controls?.institutionnelDroitPublic;
	}

	get sirenRegistreField(): AbstractControl {
		return (this.form as FormGroup<ITierPersonneMoraleForm>)?.controls?.registreType;
	}

	get adresseField(): AbstractControl {
		return this.form?.controls?.adresse;
	}

	get paysResidenceFiscalIdField(): AbstractControl {
		return (this.form as FormGroup<ITierPersonnePhysiqueForm>)?.controls?.paysResidenceFiscalId;
	}

	get sirenRadioLabelKey(): string {
		switch (this.registreType) {
			case RegistreType.Tahiti:
				return 'Labels.Tahiti';
			case RegistreType.RidSiren:
				return 'Labels.RidSiren';
			default:
				return 'Labels.Siren';
		}
	}

	get canSearchBySiren(): boolean {
		return (
			this.isSirenSelected &&
			this.isSirenRequired &&
			this.canSearchOnCurrentRegistreType &&
			!this.isSearchingBySiren &&
			this.isSearchableSiren(this.registreField.value)
		);
	}

	isSubmitButtonDisabled$ = this.store$.select(TiersStoreSelectors.selectIsSubmitButtonDisabled).subscribe(res => {
		this.isSubmited = res;
	});

	constructor(
		protected store$: Store,
		private tiersService: TiersService
	) {
		super();
	}

	ngOnInit() {
		this.form = new FormGroup<ITierPersonnePhysiqueForm | ITierPersonneMoraleForm>({
			// Personne Physique
			prenom: new FormControl<string | null>('', Validators.maxLength(49)),
			nom: new FormControl<string | null>('', [Validators.required, Validators.maxLength(49)]),
			dateNaissance: new FormControl<Date | null>(null),
			isBirthDateUnknown: new FormControl<boolean | null>(false),
			// Personne Morale
			registre: new FormControl<string | null>('', Validators.maxLength(this.maxRegistreFielLength)),
			typeImmatriculation: new FormControl<TypeImmatriculation>(TypeImmatriculation.Siren),
			libelle: new FormControl<string | null>('', [Validators.required, Validators.maxLength(30)]),
			formeJuridique: new FormControl<string | null>('', Validators.maxLength(11)),
			denominationCommerciale: new FormControl<string | null>('', Validators.maxLength(100)),
			institutionnelDroitPublic: new FormControl<boolean | null>(null),
			registreType: new FormControl<RegistreType | null>(null),
			// Shared
			paysId: new FormControl<string | null>('', Validators.required),
			type: new FormControl<boolean | null>(null),
			paysResidenceFiscalId: new FormControl<string | null>(null),
			adresse: new FormControl<IAdresse | null>(null),
			telephone: new FormControl<string | null>(null),
			email: new FormControl<string | null>(null, CustomValidators.formatEmailNotification()),
			tempId: new FormControl<string | null>(null)
		});

		// Remplissez le formulaire avec le modèle actuel
		if (this.personnePhysiqueModel) {
			this.form.patchValue({
				prenom: this.personnePhysiqueModel.prenom,
				nom: this.personnePhysiqueModel.nom,
				dateNaissance: this.personnePhysiqueModel.dateNaissance,
				paysId: this.personnePhysiqueModel.paysId
			});
		}

		this.isBirthDateUnknownField?.valueChanges.subscribe(checked => {
			this.handleIsBirthDateUnknown(checked);
		});

		this.registreField.valueChanges.pipe(takeUntil(this.destroySubscriptions$)).subscribe(value => {
			if (this.registreField.dirty && this.isSearchingBySiren) {
				this.sirenSearchErrorMessage = null;
				this.libelleField.reset();
			}
		});

		this.libelleField.valueChanges.pipe(takeUntil(this.destroySubscriptions$)).subscribe(value => {
			if (value && !!this.sirenSearchErrorMessage) {
				this.sirenSearchErrorMessage = null;
			}
		});

		this.store$.dispatch(TiersStoreActions.SetDisableSubmitButton({ param: false }));
	}

	ngOnChanges() {
		this.inputRestrictionValue =
			this.registreType === RegistreType.Siren || this.registreType === RegistreType.RidSiren
				? 'numericWithwhiteSpace'
				: this.setInputRestriction();
	}

	buildForm(tiersType: TiersType): void {
		const paysResidenceId = this.form?.controls?.adresse?.get('paysResidenceId')?.value;

		this.form?.controls?.adresse?.patchValue({
			paysResidenceId: paysResidenceId
		});

		switch (tiersType) {
			case TiersType.PersonnePhysique:
				this.form.patchValue({
					prenom: '',
					nom: '',
					dateNaissance: null,
					paysId: null,
					telephone: null,
					email: null
				});
				this.formControlNames = extractFormControlNames<ITiersPersonnePhysiqueForm>(this.form);
				break;
			case TiersType.PersonneMorale:
				this.form.patchValue({
					registre: '',
					typeImmatriculation: TypeImmatriculation.Siren,
					libelle: '',
					paysId: null,
					formeJuridique: '',
					denominationCommerciale: '',
					institutionnelDroitPublic: null,
					telephone: null,
					email: null
				});
				this.formControlNames = extractFormControlNames<ITiersPersonneMoraleForm>(this.form);
				break;
			default:
				break;
		}
		this.updateValidators(tiersType);
		this.paysIdField.valueChanges.pipe(takeUntil(this.destroySubscriptions$)).subscribe(() => {
			this.form.patchValue({ registre: null });
			this.setSirenValidators();
		});
	}

	setPhoneNumber(storedPhone: string) {
		if (storedPhone == null) return null;
		const parsed = parsePhoneNumberFromString(storedPhone);
		if (!parsed) return null;
		this.selectedCountryISO = parsed.country?.toLowerCase() ?? this.selectedCountryISO;
		return parsed.nationalNumber;
	}

	buildConsultForm(tiers: ITiersWithAdress) {
		switch (tiers?.type) {
			case TiersType.PersonnePhysique:
				this.isPersonnePhysique = true;
				this.isPersonneMorale = false;

				this.form.patchValue({
					prenom: tiers?.prenom,
					nom: tiers?.nom,
					dateNaissance: tiers?.dateNaissance ? new Date(tiers?.dateNaissance) : null,
					isBirthDateUnknown: tiers?.isBirthDateUnknown ?? false,
					paysResidenceFiscalId: tiers?.paysResidenceFiscalId ?? tiers?.paysResidenceFiscale?.id,
					telephone: this.setPhoneNumber(tiers?.telephone),
					email: tiers?.email,
					adresse: tiers?.adresse,
					paysId: tiers?.paysId ?? tiers?.pays?.id,
					tempId: tiers?.tempId
				});
				this.updateValidators(tiers?.type);
				break;

			case TiersType.PersonneMorale:
				this.isPersonnePhysique = false;
				this.isPersonneMorale = true;

				this.form.patchValue({
					libelle: tiers?.libelle,
					formeJuridique: tiers?.formeJuridique,
					denominationCommerciale: tiers?.denominationCommerciale,
					institutionnelDroitPublic: tiers?.institutionnelDroitPublic,
					telephone: this.setPhoneNumber(tiers?.telephone),
					email: tiers?.email,
					adresse: tiers?.adresse,
					tempId: tiers?.tempId
				});
				this.updateValidators(tiers?.type);
				this.registreValue = tiers?.registre; // le numero immatriculation

				// checked input
				switch (tiers?.typeImmatriculation) {
					case TypeImmatriculation.Siren:
						this.isSirenSelected = true;
						this.onSirenSelected();
						break;
					case TypeImmatriculation.RegistreEquivalent:
						this.isRegEqSelected = true;
						this.onRegEqSelected();
						break;
					case TypeImmatriculation.Imo:
						this.isImoSelected = true;
						this.onImoSelected();
						break;

					default:
						break;
				}
				break;
			default:
				break;
		}
		// propriete commune
		this.selectedPay = tiers?.pays?.id ?? tiers?.paysId; // 'nationalite' si personne physique, 'pays siege social' si personne morale
		this.currentAdresse = tiers?.adresse;
		this.form.disable();
	}

	getModelFromForm(): ITiersPersonnePhysiqueForm | ITiersPersonneMoraleForm {
		if (this.isPersonnePhysique) {
			return {
				prenom: this.prenomField.value,
				nom: this.nomField.value?.toUpperCase(),
				dateNaissance: dateToUTC(this.dateNaissanceField?.value),
				isBirthDateUnknown: this.isBirthDateUnknownField.value,
				paysId: this.paysIdField?.value,
				type: TiersType.PersonnePhysique,
				adresse: this.adresseField?.value,
				paysResidenceFiscalId: this.paysResidenceFiscalIdField?.value,
				telephone: this.telephoneField?.value?.number,
				email: this.emailField?.value,
				tempId: this.tempId ?? crypto.randomUUID() // A temporary ID generated on the frontend to ensure uniqueness of tiers dtos
			}; // Un ITiersPersonnePhysiqueForm
		} else {
			return {
				libelle: this.libelleField.value?.toUpperCase(),
				registre: this.registreField?.value?.toString(),
				typeImmatriculation: this.typeImmatriculationField?.value,
				denominationCommerciale: this.denominationCommercialeField?.value?.toUpperCase(),
				formeJuridique: this.formeJuridiqueField?.value
					?.toUpperCase()
					.normalize('NFD')
					.replace(/[\u0300-\u036f]/g, ''),
				institutionnelDroitPublic: this.institutionnelDroitPublicField?.value,
				sirenRegistre: this.sirenRegistreField?.value,
				paysId: this.paysIdField?.value,
				type: TiersType.PersonneMorale,
				adresse: this.adresseField?.value,
				telephone: this.telephoneField?.value?.number,
				email: this.emailField?.value,
				tempId: this.tempId ?? crypto.randomUUID() // A temporary ID generated on the frontend to ensure uniqueness of tiers dtos
			}; // Un ITiersPersonneMoraleForm
		}
	}

	onClose(): void {
		this.action.emit({ type: ActionType.Close });
	}

	onSubmit(): void {
		const tiersType = this.isPersonnePhysique ? TiersType.PersonnePhysique : TiersType.PersonneMorale;
		this.updateValidators(tiersType);
		if (this.form.valid) {
			if (this.isPersonneMorale) {
				if (this.isSirenRequired && this.registreField.value === '') {
					this.showValidationErrorMessage = true;
				} else {
					this.action.emit({
						type: ActionType.Submit,
						value: this.getModelFromForm()
					});
				}
			} else if (this.isPersonnePhysique) {
				this.action.emit({
					type: ActionType.Submit,
					value: this.getModelFromForm()
				});
			} else {
				this.showValidationErrorMessage = true;
			}
			this.store$.dispatch(TiersStoreActions.SetDisableSubmitButton({ param: true }));
		} else {
			this.showValidationErrorMessage = false;
			markFormGroupTouched(this.form as FormGroup<ITierPersonnePhysiqueForm | ITierPersonneMoraleForm>);
		}
	}

	onChangeTierType(tiersType: TiersType): void {
		switch (tiersType) {
			case TiersType.PersonnePhysique:
				this.isPersonnePhysique = true;
				this.isPersonneMorale = false;
				break;
			case TiersType.PersonneMorale:
				this.isPersonnePhysique = false;
				this.isPersonneMorale = true;
				this.showValidationErrorMessage = true;
				break;
			default:
				break;
		}
		markFormGroupUntouched(this.form as FormGroup<ITierPersonnePhysiqueForm | ITierPersonneMoraleForm>);
		this.buildForm(tiersType);
	}

	onSirenSelected(): void {
		this.form.patchValue({ registre: '' });
		if (this.registreType !== RegistreType.RegistreEquivalent) {
			this.selectSirenImmatriculationType(this.registreType);
		} else {
			this.selectImmatriculationType(true, false, false, TypeImmatriculation.Siren, 9, 'WrongSirenLength');
		}
	}

	onRegEqSelected(): void {
		this.form.patchValue({ registre: '' });
		this.selectImmatriculationType(false, true, false, TypeImmatriculation.RegistreEquivalent, 50, 'WrongRegistreEquivalentLength');
	}

	onImoSelected(): void {
		this.form.patchValue({ registre: '' });
		this.selectImmatriculationType(false, false, true, TypeImmatriculation.Imo, 20, 'WrongImoLength');
	}

	selectImmatriculationType(
		isSirenSelected: boolean,
		isRegEqSelected: boolean,
		isImoSelected: boolean,
		typeImmatriculation: TypeImmatriculation,
		maxLength: number,
		errorMessage: string
	): void {
		this.sirenSearchErrorMessage = null;
		this.isSearchingBySiren = false;

		this.isSirenSelected = isSirenSelected;
		this.isRegEqSelected = isRegEqSelected;
		this.isImoSelected = isImoSelected;
		this.immatriculationErrorMessage = `ValidationErrors.${errorMessage}`;
		this.maxRegistreFielLength = maxLength;
		this.form.patchValue({ typeImmatriculation: typeImmatriculation });

		this.registreField.clearValidators();

		if (this.isPaysSiren && !this.institutionnelDroitPublicField.value) {
			this.registreField?.addValidators(Validators.required);
		}

		if (this.isSirenSelected) {
			if (this.isPaysParentFr) this.registreField?.addValidators(CustomValidators.exactLengths(this.paysParentMaxLengthSiren));
			else this.registreField?.addValidators(CustomValidators.exactLength(maxLength));
		}

		if (this.isRegEqSelected || this.isImoSelected) {
			this.registreField?.addValidators(Validators.maxLength(maxLength));
		}

		this.registreField?.updateValueAndValidity();
	}

	onInputRegistre(event: Event): void {
		this.registreValue = this.registreValue?.toUpperCase();
		this.showValidationErrorMessage = false;
	}

	omitSpecialChar(event): boolean {
		let k = event.charCode;
		return (k > 64 && k < 91) || (k > 96 && k < 123) || k == 8 || k == 32 || (k >= 48 && k <= 57);
	}

	onPasteRegistre(event: ClipboardEvent): void {
		if (this.isSirenSelected) {
			event.preventDefault();
			const pastedData = event.clipboardData?.getData('text') || '';
			const cleanedData = pastedData
				.replace(/\s+/g, '')
				.replace(/[^0-9A-Za-z]/g, '')
				.toUpperCase();

			this.registreValue = cleanedData;
			this.registreField.setValue(cleanedData);
		}
	}

	setInputRestriction(): string {
		const value = this.registreField?.value;
		const isAlphanumeric = /^[a-zA-Z0-9]+$/.test(value);
		if (this.isPaysFr && isAlphanumeric) return 'numericWithwhiteSpace';
		if (!value) return null;

		const length = value.length;
		if (this.isSirenSelected && this.paysParentMaxLengthSiren.numeric.includes(length)) {
			return 'numericWithwhiteSpace';
		}
		return null;
	}

	private get canSearchOnCurrentRegistreType(): boolean {
		return this.registreType === RegistreType.Siren || this.registreType === RegistreType.RidSiren;
	}

	private normalizeRegistreValue(value: string | null | undefined): string {
		return (value ?? '').replace(/\s+/g, '');
	}

	private isSearchableSiren(value: string | null | undefined): boolean {
		const normalizedValue = this.normalizeRegistreValue(value);

		return /^\d{9}$/.test(normalizedValue) && !/^0+$/.test(normalizedValue) && this.isValidLuhnNumber(normalizedValue);
	}

	private isValidLuhnNumber(value: string): boolean {
		let sum = 0;

		for (let index = 0; index < value.length; index++) {
			let digit = parseInt(value[value.length - 1 - index], 10);

			if (Number.isNaN(digit)) {
				return false;
			}

			if (index % 2 === 1) {
				digit *= 2;
				if (digit >= 10) {
					digit = Math.floor(digit / 10) + (digit % 10);
				}
			}

			sum += digit;
		}

		return sum % 10 === 0;
	}

	async getPays(): Promise<IPays> {
		const pays = (await firstValueFrom(this.nationalites$)).find(p => p.id === this.paysIdField?.value);

		return pays;
	}

	public async setSirenValidators(): Promise<void> {
		const pays = await this.getPays();
		const registreType: RegistreType = pays?.registre ?? RegistreType.RegistreEquivalent;
		this.registreType = registreType;
		this.isPaysFr = pays?.codeISO == AppConstants.FRANCE_CODE_ISO;
		this.isPaysParentFr = false; // La règle liée au pays parent est supprimée (RG2)

		const isSpecificRegistre = registreType !== RegistreType.RegistreEquivalent;
		this.isPaysSiren = isSpecificRegistre;
		this.isSirenRequired = isSpecificRegistre && !this.institutionnelDroitPublicField.value && this.isPersonneMorale;

		if (!this.isSirenRequired) {
			this.registreField?.removeValidators(Validators.required);
			if (this.isPersonneMorale) {
				if (this.institutionnelDroitPublicField?.value && isSpecificRegistre) {
					this.selectSirenImmatriculationType(registreType);
				} else {
					this.selectImmatriculationType(
						false,
						true,
						false,
						TypeImmatriculation.RegistreEquivalent,
						50,
						'WrongRegistreEquivalentLength'
					);
				}
			}
		} else {
			this.selectSirenImmatriculationType(registreType);
		}

		this.registreField?.updateValueAndValidity();

		// Mettre à jour la restriction de saisie selon le type de registre
		this.inputRestrictionValue =
			registreType === RegistreType.Siren || registreType === RegistreType.RidSiren ? 'numericWithwhiteSpace' : null;
	}

	/**
	 * Sélectionne le type d'immatriculation SIREN/Tahiti/RidSiren et applique
	 * les validateurs correspondants (longueur exacte + algorithme de Luhn).
	 */
	private selectSirenImmatriculationType(registreType: RegistreType): void {
		this.sirenSearchErrorMessage = null;
		this.isSearchingBySiren = false;
		this.isSirenSelected = true;
		this.isRegEqSelected = false;
		this.isImoSelected = false;
		this.form.patchValue({ typeImmatriculation: TypeImmatriculation.Siren });

		this.registreField.clearValidators();

		if (this.isSirenRequired) {
			this.registreField?.addValidators(Validators.required);
		}

		switch (registreType) {
			case RegistreType.Siren:
				this.immatriculationErrorMessage = 'ValidationErrors.WrongSirenLength';
				this.luhnErrorMessage = 'ValidationErrors.WrongSirenLuhn';
				this.registreField?.addValidators([CustomValidators.exactLength(9), CustomValidators.luhn()]);
				break;
			case RegistreType.RidSiren:
				this.immatriculationErrorMessage = 'ValidationErrors.WrongRidSirenLength';
				this.luhnErrorMessage = 'ValidationErrors.WrongRidSirenLuhn';
				this.registreField?.addValidators([CustomValidators.exactLengths({ numeric: [7, 9] }), CustomValidators.luhn()]);
				break;
			case RegistreType.Tahiti:
				this.immatriculationErrorMessage = 'ValidationErrors.WrongTahitiLength';
				this.luhnErrorMessage = 'ValidationErrors.WrongTahitiLuhn';
				this.registreField?.addValidators([CustomValidators.exactLength(6), CustomValidators.luhn(true)]);
				break;
		}

		this.registreField?.updateValueAndValidity();
	}

	onChangeAdresse(adresse: IAdresse): void {
		this.adresseField?.patchValue(adresse);
	}

	private updateValidators(tiersType: TiersType): void {
		if (tiersType === TiersType.PersonnePhysique) {
			this.prenomField?.addValidators([requiredNotBlank, Validators.maxLength(100)]);
			this.nomField?.addValidators([requiredNotBlank, Validators.maxLength(100)]);
			this.libelleField?.clearValidators();
			this.registreField?.clearValidators();
			this.formeJuridiqueField?.clearValidators();
			this.denominationCommercialeField?.clearValidators();
		} else {
			this.registreField?.addValidators(Validators.maxLength(this.maxRegistreFielLength));
			this.libelleField?.addValidators([requiredNotBlank, Validators.maxLength(30)]);
			this.formeJuridiqueField?.addValidators(Validators.maxLength(11));
			this.denominationCommercialeField?.addValidators(Validators.maxLength(100));
			this.prenomField?.clearValidators();
			this.dateNaissanceField?.clearValidators();
			this.nomField?.clearValidators();
		}

		this.prenomField?.updateValueAndValidity();
		this.dateNaissanceField?.updateValueAndValidity();
		this.nomField?.updateValueAndValidity();
		this.libelleField?.updateValueAndValidity();
		this.registreField?.updateValueAndValidity();
		this.formeJuridiqueField?.updateValueAndValidity();
		this.denominationCommercialeField?.updateValueAndValidity();
	}

	hideAutoFillButton() {
		const inputElement = this.denominationCommerciale.nativeElement;

		setTimeout(() => {
			const autoFillButton = inputElement.shadowRoot.querySelector('input::-webkit-contacts-auto-fill-button');
			if (autoFillButton) {
				autoFillButton.style.display = 'none';
				autoFillButton.style.visibility = 'hidden';
				autoFillButton.style.pointerEvents = 'none';
			}
		}, 0);
	}

	handleIsBirthDateUnknown(checked: boolean) {
		if (checked) {
			this.dateNaissanceField.patchValue(null);
			this.dateNaissanceField.disable();
			this.dateNaissanceField.clearValidators();
			this.isBirthDateRequired = false;
		} else {
			this.dateNaissanceField.enable();
			this.dateNaissanceField?.addValidators([Validators.required]);
			this.isBirthDateRequired = true;
		}
		this.dateNaissanceField.updateValueAndValidity();
	}

	onSearchBySiren(): void {
		if (!this.canSearchBySiren) {
			return;
		}

		const siren = this.normalizeRegistreValue(this.registreField.value);

		this.isSearchingBySiren = true;
		this.sirenSearchErrorMessage = null;

		this.tiersService.getNameBySiren(siren).subscribe({
			next: (response: INameBySiret) => {
				if (response?.name) {
					this.libelleField.setValue(response.name.substring(0, 30));
					this.sirenSearchErrorMessage = null;
				} else if (response?.isRateLimited) {
					this.sirenSearchErrorMessage = 'Le service est temporairement indisponible, veuillez réessayer dans quelques instants.';
				} else {
					this.sirenSearchErrorMessage = 'Dénomination sociale non trouvée';
				}
				this.isSearchingBySiren = false;
			},
			error: err => {
				this.sirenSearchErrorMessage = 'Dénomination sociale non trouvée';
				this.isSearchingBySiren = false;
			}
		});
	}
}
